import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { FileText, Plus, Trash2, Pencil, Paperclip, X, Printer, MapPin, LayoutGrid, List as ListIcon, Search } from "lucide-react";
import { toast } from "sonner";
import {
  signAttachments,
  attachmentPath,
  SIGNED_URL_TTL,
  type PostOrderAttachment,
} from "@/lib/postOrderAttachments";

export const Route = createFileRoute("/_authenticated/post-orders")({
  component: PostOrdersPage,
  head: () => ({ meta: [{ title: "Post Orders — MissionReady" }] }),
});

type PostOrder = {
  id: string; site_id: string; title: string; body: string | null;
  checklist: string[]; attachments: PostOrderAttachment[];
  active: boolean;
};
type Site = {
  id: string; name: string;
  address: string | null; city: string | null; region: string | null;
  postal_code: string | null; country: string | null;
};

const IMAGE_RX = /\.(png|jpe?g|gif|webp|heic|heif|avif|bmp|svg)(\?|$)/i;
const isImage = (a: PostOrderAttachment) =>
  IMAGE_RX.test(a.name) || IMAGE_RX.test(a.url);


const formatAddress = (s: Site): string => {
  const line2 = [s.city, s.region, s.postal_code].filter(Boolean).join(", ");
  return [s.address, line2, s.country].filter(Boolean).join(" • ");
};

function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let index = lowerText.indexOf(lowerQuery, lastIndex);
  while (index !== -1) {
    if (index > lastIndex) parts.push(text.slice(lastIndex, index));
    parts.push(
      <mark key={index} className="rounded bg-yellow-400/90 px-0.5 text-black font-medium">
        {text.slice(index, index + query.length)}
      </mark>
    );
    lastIndex = index + query.length;
    index = lowerText.indexOf(lowerQuery, lastIndex);
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

function PostOrdersPage() {
  const { currentOrg, currentRole, isPlatformAdmin, user } = useAuth();
  const canEdit = isPlatformAdmin || ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");
  const [orders, setOrders] = useState<PostOrder[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [siteFilter, setSiteFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"grouped" | "list">("grouped");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PostOrder | null>(null);
  const [form, setForm] = useState<Partial<PostOrder>>({ active: true, checklist: [], attachments: [] });
  const [newCheck, setNewCheck] = useState("");

  const load = async () => {
    if (!currentOrg) return;
    const [{ data: o }, { data: s }] = await Promise.all([
      supabase.from("post_orders").select("*").eq("organization_id", currentOrg.id).order("updated_at", { ascending: false }),
      supabase.from("sites").select("id,name,address,city,region,postal_code,country").eq("organization_id", currentOrg.id).order("name"),
    ]);
    const normalizeChecklist = (raw: unknown): string[] => {
      if (!Array.isArray(raw)) return [];
      return raw
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object" && "text" in item) return String((item as { text: unknown }).text ?? "");
          return "";
        })
        .filter((t) => t.length > 0);
    };
    const mapped = await Promise.all((o ?? []).map(async (r) => ({
      ...r,
      checklist: normalizeChecklist(r.checklist),
      attachments: await signAttachments(((r.attachments as PostOrderAttachment[]) ?? [])),
    })));
    setOrders(mapped as PostOrder[]);
    setSites((s ?? []) as Site[]);
  };
  useEffect(() => { load(); }, [currentOrg?.id]);

  const escapeHtml = (s: string) =>
    s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

  const printPostOrder = (po: PostOrder) => {
    const orgName = currentOrg?.name ?? "MissionReady";
    const site = siteName(po.site_id);
    const printedAt = new Date().toLocaleString();
    const items = po.checklist.map((c) => `<li><span class="box"></span><span class="item">${escapeHtml(c)}</span></li>`).join("");
    const body = po.body ? `<section class="body"><h2>Briefing</h2><p>${escapeHtml(po.body).replace(/\n/g, "<br/>")}</p></section>` : "";
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(po.title)} — Dispatch Checklist</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #111; background: #fff; }
  .page { max-width: 720px; margin: 0 auto; padding: 32px 36px; }
  header { border-bottom: 2px solid #CC0000; padding-bottom: 12px; margin-bottom: 20px; }
  header .org { font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; color: #666; }
  header h1 { font-size: 22px; margin: 4px 0 6px; }
  header .meta { font-size: 12px; color: #444; display: flex; gap: 16px; flex-wrap: wrap; }
  header .meta strong { color: #111; }
  .body { margin-bottom: 18px; }
  .body h2, .checklist h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.06em; color: #CC0000; margin: 0 0 8px; }
  .body p { font-size: 13px; line-height: 1.5; margin: 0; white-space: pre-wrap; }
  .checklist ul { list-style: none; padding: 0; margin: 0; }
  .checklist li { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px dashed #ddd; font-size: 13px; line-height: 1.4; }
  .checklist .box { display: inline-block; width: 14px; height: 14px; border: 1.5px solid #333; border-radius: 2px; flex-shrink: 0; margin-top: 2px; }
  .checklist .item { flex: 1; }
  .signoff { margin-top: 28px; padding-top: 12px; border-top: 1px solid #999; display: grid; grid-template-columns: 1fr 1fr; gap: 24px; font-size: 12px; color: #333; }
  .signoff .field { border-bottom: 1px solid #333; padding-bottom: 18px; margin-bottom: 4px; }
  .signoff label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #666; }
  footer { margin-top: 18px; font-size: 10px; color: #888; text-align: center; }
  @media print {
    .no-print { display: none; }
    .page { padding: 16px 24px; }
    @page { margin: 14mm; }
  }
  .toolbar { position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; }
  .toolbar button { background: #CC0000; color: #fff; border: 0; padding: 8px 14px; font-size: 13px; border-radius: 4px; cursor: pointer; }
  .toolbar button.secondary { background: #444; }
</style></head><body>
<div class="toolbar no-print">
  <button onclick="window.print()">Print / Save as PDF</button>
  <button class="secondary" onclick="window.close()">Close</button>
</div>
<div class="page">
  <header>
    <div class="org">${escapeHtml(orgName)}</div>
    <h1>${escapeHtml(po.title)}</h1>
    <div class="meta">
      <span><strong>Site:</strong> ${escapeHtml(site)}</span>
      <span><strong>Printed:</strong> ${escapeHtml(printedAt)}</span>
      ${!po.active ? '<span style="color:#CC0000"><strong>INACTIVE</strong></span>' : ""}
    </div>
  </header>
  ${body}
  <section class="checklist">
    <h2>Dispatch Checklist</h2>
    ${items ? `<ul>${items}</ul>` : '<p style="font-size:12px;color:#666">No checklist items.</p>'}
  </section>
  <section class="signoff">
    <div><div class="field"></div><label>Officer name & signature</label></div>
    <div><div class="field"></div><label>Date / time completed</label></div>
  </section>
  <footer>Generated by MissionReady — keep this copy for offline reference.</footer>
</div>
<script>setTimeout(function(){ window.print(); }, 250);</script>
</body></html>`;
    const w = window.open("", "_blank", "width=820,height=900");
    if (!w) {
      toast.error("Pop-up blocked. Allow pop-ups for this site to print.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };


  const startCreate = (presetSiteId?: string) => {
    setEditing(null);
    setForm({ active: true, checklist: [], attachments: [], site_id: presetSiteId });
    setOpen(true);
  };
  const startEdit = (po: PostOrder) => { setEditing(po); setForm(po); setOpen(true); };

  const uploadAttach = async (file: File) => {
    if (!currentOrg || !user) return;
    const path = `${currentOrg.id}/${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from("post-orders").upload(path, file);
    if (error) return toast.error(error.message);
    const { data } = await supabase.storage.from("post-orders").createSignedUrl(path, SIGNED_URL_TTL);
    setForm((f) => ({ ...f, attachments: [...(f.attachments ?? []), { name: file.name, path, url: data?.signedUrl ?? "" }] }));
  };

  const save = async () => {
    if (!currentOrg || !form.title || !form.site_id) return toast.error("Title and site required");
    const payload = {
      organization_id: currentOrg.id, site_id: form.site_id,
      title: form.title, body: form.body ?? null,
      checklist: form.checklist ?? [],
      // Persist only the storage path — never a bearer link.
      attachments: (form.attachments ?? []).map((a) => {
        const p = attachmentPath(a);
        return p ? { name: a.name, path: p, url: "", description: a.description ?? null } : a;
      }),
      active: form.active ?? true,
    };
    const { error } = editing
      ? await supabase.from("post_orders").update(payload).eq("id", editing.id)
      : await supabase.from("post_orders").insert(payload);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Updated" : "Created");
    setOpen(false); load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this post order?")) return;
    const { error } = await supabase.from("post_orders").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted"); load();
  };

  const siteName = (sid: string) => sites.find((s) => s.id === sid)?.name ?? "—";

  const siteMatchesSearch = (s: Site, q: string) => {
    if (!q.trim()) return true;
    const term = q.toLowerCase();
    const hay = [s.name, s.address, s.city, s.region, s.postal_code, s.country]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(term);
  };

  const filtered = useMemo(() => {
    let list = siteFilter === "all" ? orders : orders.filter((o) => o.site_id === siteFilter);
    if (search.trim()) {
      list = list.filter((o) => siteMatchesSearch(sites.find((s) => s.id === o.site_id)!, search));
    }
    return list;
  }, [orders, siteFilter, sites, search]);

  const visibleSites = useMemo(() => {
    let list = siteFilter === "all" ? sites : sites.filter((s) => s.id === siteFilter);
    if (search.trim()) list = list.filter((s) => siteMatchesSearch(s, search));
    return list;
  }, [sites, siteFilter, search]);

  const ordersBySite = useMemo(() => {
    const map: Record<string, PostOrder[]> = {};
    for (const po of filtered) (map[po.site_id] ||= []).push(po);
    return map;
  }, [filtered]);

  const renderAttachments = (atts: PostOrderAttachment[]) => {
    const images = atts.filter(isImage);
    const files = atts.filter((a) => !isImage(a));
    return (
      <>
        {images.length > 0 && (
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {images.map((a, i) => (
              <figure key={i} className="min-w-0">
                <a href={a.url} target="_blank" rel="noreferrer" title={a.description || a.name}
                   className="group relative block aspect-square overflow-hidden rounded-md border border-white/10 bg-black/30">
                  <img src={a.url} alt={a.description || a.name} loading="lazy"
                       className="h-full w-full object-cover transition group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1.5 py-0.5 text-[10px] text-white">{a.name}</div>
                </a>
                {a.description && (
                  <figcaption className="mt-1 whitespace-pre-wrap break-words text-[11px] leading-snug text-white/70">
                    {a.description}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        )}
        {files.length > 0 && (
          <div className="mt-2 flex flex-col gap-2">
            {files.map((a, i) => (
              <div key={i} className="min-w-0">
                <a href={a.url} target="_blank" rel="noreferrer"
                   className="inline-flex max-w-full items-center gap-1 rounded border border-white/10 bg-white/5 px-2 py-1 text-xs text-white hover:bg-white/10">
                  <Paperclip className="h-3 w-3 shrink-0" /> <span className="truncate">{a.name}</span>
                </a>
                {a.description && (
                  <p className="mt-1 whitespace-pre-wrap break-words text-[11px] leading-snug text-white/70">{a.description}</p>
                )}
              </div>
            ))}
          </div>
        )}

      </>
    );
  };

  const renderPostOrderCard = (po: PostOrder, opts: { showSiteBadge?: boolean } = {}) => (
    <div key={po.id} className="rounded-lg border border-white/10 bg-white/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-white">{po.title}</h3>
            {opts.showSiteBadge && (
              <Badge className="bg-[#CC0000]/15 text-[#CC0000] border border-[#CC0000]/30"><HighlightText text={siteName(po.site_id)} query={search} /></Badge>
            )}
            {!po.active && <Badge variant="outline" className="text-white/50">inactive</Badge>}
          </div>
          {po.body && <p className="mt-1 text-sm text-white/70 whitespace-pre-wrap">{po.body}</p>}
          {po.checklist.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm text-white/80">
              {po.checklist.map((c, i) => <li key={i}>☐ {c}</li>)}
            </ul>
          )}
          {po.attachments.length > 0 && renderAttachments(po.attachments)}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="icon" variant="ghost" onClick={() => printPostOrder(po)} title="Print / Save as PDF">
            <Printer className="h-4 w-4" />
          </Button>
          {canEdit && (
            <>
              <Button size="icon" variant="ghost" onClick={() => startEdit(po)} aria-label={`Edit post order ${po.title}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(po.id)} aria-label={`Delete post order ${po.title}`}><Trash2 className="h-4 w-4 text-[#CC0000]" /></Button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FileText className="h-6 w-6 text-[#CC0000]" />
          <div>
            <h1 className="text-2xl font-bold text-white">Post Orders</h1>
            <p className="text-sm text-white/60">Site-specific standing orders</p>
          </div>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => startCreate()} className="bg-[#CC0000] hover:bg-[#a30000] text-white">
                <Plus className="h-4 w-4 mr-1" /> New
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Post Order" : "New Post Order"}</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div>
                  <Label>Site *</Label>
                  <Select value={form.site_id ?? ""} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                    <SelectTrigger aria-label="Site *"><SelectValue placeholder="Choose site" /></SelectTrigger>
                    <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Title *</Label><Input aria-label="Title *" value={form.title ?? ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Body</Label><Textarea aria-label="Body" rows={6} value={form.body ?? ""} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
                <div>
                  <Label>Checklist</Label>
                  <div className="flex gap-2 mb-2">
                    <Input aria-label="Checklist" value={newCheck} onChange={(e) => setNewCheck(e.target.value)} placeholder="Add checklist item" onKeyDown={(e) => { if (e.key === "Enter" && newCheck.trim()) { setForm({ ...form, checklist: [...(form.checklist ?? []), newCheck.trim()] }); setNewCheck(""); } }} />
                    <Button type="button" variant="outline" onClick={() => { if (newCheck.trim()) { setForm({ ...form, checklist: [...(form.checklist ?? []), newCheck.trim()] }); setNewCheck(""); } }}>Add</Button>
                  </div>
                  <ul className="space-y-1">
                    {(form.checklist ?? []).map((c, i) => (
                      <li key={i} className="flex items-center justify-between text-sm text-white bg-black/20 rounded px-2 py-1">
                        <span>{c}</span>
                        <button onClick={() => setForm({ ...form, checklist: (form.checklist ?? []).filter((_, j) => j !== i) })}><X className="h-3 w-3 text-[#CC0000]" /></button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <Label className="mb-1 block">Attachments (photos of doors, maps, etc.)</Label>
                  <p className="mb-2 text-xs text-white/50">
                    Each photo or file can have its own optional description. Leave it blank to keep things as they are.
                  </p>
                  {(form.attachments ?? []).length > 0 && (
                    <div className="mb-2 space-y-2">
                      {(form.attachments ?? []).map((a, i) => (
                        <div key={i} className="flex items-start gap-3 rounded-md border border-white/10 bg-white/5 p-2">
                          {isImage(a) ? (
                            <img src={a.url} alt={a.name} className="h-16 w-16 shrink-0 rounded object-cover" />
                          ) : (
                            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-black/30 text-white/60">
                              <Paperclip className="h-5 w-5" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1 space-y-1">
                            <div className="truncate text-xs text-white/80">{a.name}</div>
                            <Textarea
                              rows={2}
                              maxLength={500}
                              placeholder="Description (optional) — e.g. Rear loading dock door, keep locked after 6pm"
                              value={a.description ?? ""}
                              aria-label={`Description for ${a.name}`}
                              onChange={(e) => {
                                const description = e.target.value;
                                setForm((f) => ({
                                  ...f,
                                  attachments: (f.attachments ?? []).map((x, j) => (j === i ? { ...x, description } : x)),
                                }));
                              }}
                              className="min-h-0 text-xs"
                            />
                          </div>
                          <button type="button"
                                  aria-label={`Remove ${a.name}`}
                                  onClick={() => setForm({ ...form, attachments: (form.attachments ?? []).filter((_, j) => j !== i) })}
                                  className="rounded-full bg-black/50 p-1 text-white hover:bg-[#CC0000]">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="inline-flex items-center gap-2 cursor-pointer rounded border border-dashed border-white/30 px-3 py-2 text-sm text-white/70 hover:bg-white/5">
                    <Paperclip className="h-4 w-4" /> Add photo or file
                    <input type="file" accept="image/*,application/pdf,.doc,.docx,.txt" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttach(f); e.target.value = ""; }} />
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <input type="checkbox" id="active" checked={form.active ?? true} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                  <Label htmlFor="active">Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save} className="bg-[#CC0000] hover:bg-[#a30000]">{editing ? "Save" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-white/70">Site:</Label>
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger aria-label="Site:" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sites</SelectItem>
            {sites.map((s) => <SelectItem key={s.id} value={s.id} textValue={s.name}><HighlightText text={s.name} query={search} /></SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search site name or address…"
            className="pl-8 w-64 text-sm"
          />
        </div>
        <div className="ml-auto flex items-center rounded-md border border-white/10 bg-white/5 p-0.5">
          <Button size="sm" variant={view === "grouped" ? "secondary" : "ghost"}
                  onClick={() => setView("grouped")} className="h-7 gap-1 text-xs">
            <LayoutGrid className="h-3.5 w-3.5" /> By site
          </Button>
          <Button size="sm" variant={view === "list" ? "secondary" : "ghost"}
                  onClick={() => setView("list")} className="h-7 gap-1 text-xs">
            <ListIcon className="h-3.5 w-3.5" /> List
          </Button>
        </div>
      </div>

      {view === "list" ? (
        <div className="space-y-3">
          {filtered.length === 0 && <p className="text-sm text-white/50">No post orders yet.</p>}
          {filtered.map((po) => renderPostOrderCard(po, { showSiteBadge: true }))}
        </div>
      ) : (
        <div className="space-y-6">
          {visibleSites.length === 0 && (
            <p className="text-sm text-white/50">No sites yet — add a site to start creating post orders.</p>
          )}
          {visibleSites.map((s) => {
            const list = ordersBySite[s.id] ?? [];
            const addr = formatAddress(s);
            return (
              <section key={s.id} className="rounded-xl border border-white/10 bg-white/[0.03]">
                <header className="flex items-start justify-between gap-3 border-b border-white/10 p-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-lg font-semibold text-white"><HighlightText text={s.name} query={search} /></h2>
                      <Badge variant="outline" className="text-white/60 border-white/20 text-[11px]">
                        {list.length} {list.length === 1 ? "order" : "orders"}
                      </Badge>
                    </div>
                    {addr ? (
                      <p className="mt-1 flex items-center gap-1 text-sm text-white/60">
                        <MapPin className="h-3.5 w-3.5 shrink-0" /> <HighlightText text={addr} query={search} />
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-white/40 italic">No address on file</p>
                    )}
                  </div>
                  {canEdit && (
                    <Button size="sm" onClick={() => startCreate(s.id)}
                            className="bg-[#CC0000] hover:bg-[#a30000] text-white shrink-0">
                      <Plus className="h-4 w-4 mr-1" /> Add post order
                    </Button>
                  )}
                </header>
                <div className="space-y-3 p-4">
                  {list.length === 0 ? (
                    <p className="text-sm text-white/50">No post orders for this site yet.</p>
                  ) : (
                    list.map((po) => renderPostOrderCard(po))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
